import axios from 'axios';

const isDev = process.env.NODE_ENV === 'development';

// Set base URL for API requests
axios.defaults.baseURL = process.env.REACT_APP_API_URL || '';

// Keep track of refresh token promise to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

// PERFORMANCE: Response caching
const cache = new Map();
const CACHE_DURATION = 30000; // 30 seconds cache

const getCacheKey = (config) => {
    const url = config.url || '';
    const method = config.method || 'get';
    const params = JSON.stringify(config.params || {});
    return `${method}:${url}:${params}`;
};

const shouldCache = (url, method) => {
    // Only cache GET requests
    if (method !== 'get') return false;

    // Cache these endpoints
    const cacheableEndpoints = [
        '/api/sessions/',
        '/api/players/',
        '/api/locations/',
        '/api/stakes/',
        '/api/bankroll/',
        '/api/mistake-types/',
        '/api/leaks/'
    ];

    return cacheableEndpoints.some(endpoint => url?.startsWith(endpoint));
};

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// Add request interceptor to add Authorization header + check cache
axios.interceptors.request.use(
    (config) => {
        // Don't add token for refresh token requests
        if (config.url === '/api/token/refresh/') {
            return config;
        }

        const token = localStorage.getItem('access');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // PERFORMANCE: Check cache for GET requests
        const url = config.url || '';
        const method = (config.method || 'get').toLowerCase();

        if (shouldCache(url, method)) {
            const cacheKey = getCacheKey(config);
            const cachedResponse = cache.get(cacheKey);

            if (cachedResponse && Date.now() - cachedResponse.timestamp < CACHE_DURATION) {
                isDev && console.log(`✅ [CACHE HIT] ${cacheKey}`);
                // Return cached response via custom adapter
                config.adapter = () => {
                    return Promise.resolve({
                        data: cachedResponse.data,
                        status: 200,
                        statusText: 'OK',
                        headers: cachedResponse.headers || {},
                        config,
                        cached: true
                    });
                };
            }
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Helper to invalidate cache for an endpoint after mutations
const invalidateCacheForUrl = (url) => {
    if (!url) return;

    // Extract base endpoint (e.g., /api/sessions/140/ -> /api/sessions/)
    const parts = url.split('/').filter(Boolean);
    if (parts.length >= 2) {
        const baseEndpoint = `/${parts[0]}/${parts[1]}/`;
        const keys = Array.from(cache.keys());
        const matchingKeys = keys.filter(key => key.includes(baseEndpoint));
        matchingKeys.forEach(key => cache.delete(key));
        if (matchingKeys.length > 0) {
            isDev && console.log(`🗑️ [CACHE INVALIDATE] Cleared ${matchingKeys.length} entries for ${baseEndpoint}`);
        }
    }
};

// Add response interceptor to handle caching + token refresh
axios.interceptors.response.use(
    (response) => {
        const url = response.config?.url || '';
        const method = (response.config?.method || 'get').toLowerCase();

        // CACHE INVALIDATION: Clear cache after mutations (POST/PATCH/PUT/DELETE)
        if (method !== 'get' && response.config) {
            invalidateCacheForUrl(url);

            // Also clear sessions cache when mutating session-related endpoints
            const sessionRelatedEndpoints = [
                '/api/session-buyins/',
                '/api/session-checkpoints/',
                '/api/session-mistakes/',
                '/api/session-players/',
                '/api/big-hands/',
                '/api/big-folds/',
                '/api/curiosity-calls/',
                '/api/state-logs/',
                '/api/table-changes/'
            ];
            if (sessionRelatedEndpoints.some(endpoint => url.includes(endpoint))) {
                invalidateCacheForUrl('/api/sessions/');
            }
        }

        // PERFORMANCE: Store successful GET responses in cache
        if (response.config && !response.cached && method === 'get') {
            if (shouldCache(url, method)) {
                const cacheKey = getCacheKey(response.config);
                cache.set(cacheKey, {
                    data: response.data,
                    headers: response.headers,
                    timestamp: Date.now()
                });
                isDev && console.log(`💾 [CACHE STORE] ${cacheKey}`);
            }
        }
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // Safety check
        if (!originalRequest || !originalRequest.url) {
            return Promise.reject(error);
        }

        // If the request was for token refresh, handle it separately
        if (originalRequest.url === '/api/token/refresh/') {
            localStorage.removeItem('access');
            localStorage.removeItem('refresh');
            window.location.href = '/login';
            return Promise.reject(error);
        }

        // Only attempt refresh for 401 errors on non-auth endpoints
        if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url.startsWith('/api/token/')) {
            if (isRefreshing) {
                try {
                    const token = await new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    });
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return axios(originalRequest);
                } catch (err) {
                    return Promise.reject(err);
                }
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const refreshToken = localStorage.getItem('refresh');
                if (!refreshToken) {
                    throw new Error('No refresh token available');
                }

                const response = await axios.post('/api/token/refresh/', {
                    refresh: refreshToken
                });

                const { access } = response.data;
                if (!access) {
                    throw new Error('No access token in refresh response');
                }

                localStorage.setItem('access', access);

                // Update authorization header
                originalRequest.headers.Authorization = `Bearer ${access}`;

                processQueue(null, access);
                return axios(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);
                localStorage.removeItem('access');
                localStorage.removeItem('refresh');
                window.location.href = '/login';
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

// Export cache utilities for manual cache invalidation
export const clearCache = () => {
    cache.clear();
    isDev && console.log('🗑️ [CACHE CLEARED] All cached data cleared');
};

export const clearCacheForEndpoint = (endpoint) => {
    const keys = Array.from(cache.keys());
    const matchingKeys = keys.filter(key => key.includes(endpoint));
    matchingKeys.forEach(key => cache.delete(key));
    isDev && console.log(`🗑️ [CACHE CLEARED] Cleared ${matchingKeys.length} entries for ${endpoint}`);
};

export const getCacheStats = () => {
    return {
        size: cache.size,
        entries: Array.from(cache.keys())
    };
};

// Export axios as default for backwards compatibility
export default axios;
